/**
 * Credentials the login form fills in for itself, during a demonstration.
 *
 * Typing an address and a password in front of a room is slow and goes wrong,
 * so the form can arrive already filled. What it must not do is carry a
 * password in the source.
 *
 * So the values come from the environment and default to empty. Nothing is
 * committed; `.env.example` lists the names with no values beside them, and a
 * checkout with no `.env` entries behaves exactly as before.
 *
 * Two things are worth being plain about, because this is a login form:
 *
 *   * Vite inlines `VITE_`-prefixed variables into the bundle at build time.
 *     Anything set here is readable by anyone who opens the built JavaScript.
 *     It is a demonstration account's password, in public, on whatever host
 *     the build is served from.
 *   * So it is refused unless the build is a development build or
 *     `VITE_DEMO_PREFILL` is explicitly `true`. A production build made
 *     without that flag ignores the values even if someone has set them,
 *     which is the case this guard exists for: the demo `.env` being copied
 *     onto a real server.
 *
 * ## The address on its own
 *
 * The address may be configured without a password: the form then arrives
 * with the address filled and the password box empty, which is how this
 * demonstration is run.
 *
 * That half-filled state once led to a lockout. The form looked finished, the
 * empty password went to the server, and repeated "wrong credentials" answers
 * locked a healthy account. The form now makes the state safe itself, rather
 * than relying on a notice: the cursor starts in the password box, and an
 * empty password is never sent (see `Login.tsx`).
 *
 * A password without an address fills nothing.
 */

const prefillAllowed = (): boolean => {
  if (import.meta.env.DEV) return true;
  return String(import.meta.env.VITE_DEMO_PREFILL ?? '').toLowerCase() === 'true';
};

/**
 * The address alone, both values, or neither.
 *
 * Read together in one place, so no caller can be handed a password without
 * the address it belongs to.
 */
const configured = (): { email: string; password: string } => {
  const empty = { email: '', password: '' };
  if (!prefillAllowed()) return empty;

  const email = String(import.meta.env.VITE_DEMO_EMAIL ?? '').trim();
  // Not trimmed: leading and trailing spaces are legitimate in a password,
  // and silently removing them would fail in a way nobody could see.
  const password = String(import.meta.env.VITE_DEMO_PASSWORD ?? '');

  if (!email) return empty;
  return { email, password };
};

/** The address to put in the form, or empty. */
export function demoEmail(): string {
  return configured().email;
}

/** The password to put in the form, or empty. Read the file header first. */
export function demoPassword(): string {
  return configured().password;
}

/** True when the form was filled in (at least the address). */
export function isPrefilled(): boolean {
  return configured().email.length > 0;
}
